import { Asset, AssetType, FolderContent } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { usePopover } from '@/hooks/usePopover';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { FileUpload, useFileUpload } from '@/services/fileUpload';
import { useTenants } from '@/services/tenants/useTenants';
import { useTheme } from '@mui/material/styles';
import { Add, CameraAlt, Cancel, Close, UploadFile, ViewInAr } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  styled,
  Tooltip,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { StyledList, StyledListItem, StyledPopover } from '../AssetsPage/PopoverList';
import { AssetSelectionModal } from '../modals/AssetSelectionModal';
import { ImageEditorModal } from '../modals/ImageEditorModal/ImageEditorModal';
import { RoundedSquareIconButton } from '../RoundedSquareIconButton';
import { Thumbnail } from '../Thumbnail';
import { ALLOWED_IMAGE_FORMATS } from './utils';
import { BodyText } from '../BodyText';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const THUMBNAIL_SIZE = 64;

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  backgroundColor: theme.palette.action.hover,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover button': {
    visibility: 'visible',
  },
}));

const XButton = styled(Button)(({ theme }) => ({
  padding: '0',
  minWidth: '0',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  backgroundColor: theme.palette.text.secondary,
  position: 'absolute',
  top: '2px',
  right: '2px',
  zIndex: 1,
  visibility: 'hidden',
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
  },
}));

const CameraView = ({
  webcamRef,
  onCapture,
  onClose,
}: {
  webcamRef: React.RefObject<Webcam | null>;
  onCapture: () => void;
  onClose: () => void;
}) => {
  return (
    <>
      <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <Close />
      </IconButton>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4/3',
          overflow: 'hidden',
          borderRadius: 1,
          bgcolor: 'background.default',
        }}
      >
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat='image/jpeg'
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          videoConstraints={{
            facingMode: 'user',
          }}
        />
        <IconButton
          onClick={onCapture}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            p: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          <CameraAlt />
        </IconButton>
      </Box>
    </>
  );
};

const ImageReview = ({
  imageSrc,
  onRetake,
  onAccept,
  onClose,
}: {
  imageSrc: string;
  onRetake: () => void;
  onAccept: () => void;
  onClose: () => void;
}) => {
  return (
    <>
      <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <Close />
      </IconButton>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4/3',
          overflow: 'hidden',
          borderRadius: 1,
          bgcolor: 'background.default',
        }}
      >
        <img src={imageSrc} alt='Captured' style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 2 }}>
        <Button variant='outlined' onClick={onRetake}>
          Retake
        </Button>
        <Button variant='contained' onClick={onAccept} color='primary'>
          Use Photo
        </Button>
      </Box>
    </>
  );
};

const getRowClassName = (parameters: { row: FolderContent }) => {
  if (parameters.row.contentType === 'asset') {
    const asset = parameters.row as Asset;
    return asset.type === AssetType.Document ? 'disabled-row' : '';
  }
  return '';
};

const MAX_IMAGES = 10;

const SelectedFilesThumbnails = ({ files, onRemove }: { files: File[]; onRemove: (index: number) => void }) => {
  if (files.length === 0) return;

  return (
    <Box display='flex' flexDirection='row' alignItems='center' flexWrap='wrap' gap='6px'>
      {files.map((file, index) => (
        <ThumbnailContainer key={`${file.name}-${index}`}>
          <XButton onClick={() => onRemove(index)} size='small'>
            <Cancel sx={{ color: 'white', fontSize: '20px' }} />
          </XButton>
          <Thumbnail file={file} size={THUMBNAIL_SIZE} />
        </ThumbnailContainer>
      ))}
    </Box>
  );
};

const AddMatchMenu = ({
  canOnlySearch,
  hasFileSelected,
  hasAssetSelected,
  onSelectAsset,
  onUploadFile,
  onTakePhoto,
}: {
  canOnlySearch: boolean;
  hasFileSelected: boolean;
  hasAssetSelected: boolean;
  onSelectAsset: () => void;
  onUploadFile: () => void;
  onTakePhoto: () => void;
}) => (
  <StyledList>
    {!canOnlySearch && (
      <>
        <Tooltip title={hasFileSelected ? 'Start a new search' : ''}>
          <StyledListItem onClick={onSelectAsset}>
            <ListItemIcon>
              <ViewInAr />
            </ListItemIcon>
            <ListItemText data-testid='match-asset'>Asset</ListItemText>
          </StyledListItem>
        </Tooltip>
        <Divider />
      </>
    )}
    <Tooltip title={hasAssetSelected ? 'Start a new search' : ''}>
      <StyledListItem onClick={onUploadFile}>
        <ListItemIcon>
          <UploadFile />
        </ListItemIcon>
        <ListItemText>Images</ListItemText>
      </StyledListItem>
    </Tooltip>
    <Tooltip title={hasAssetSelected ? 'Start a new search' : ''}>
      <StyledListItem onClick={onTakePhoto}>
        <ListItemIcon>
          <CameraAlt />
        </ListItemIcon>
        <ListItemText>Photos</ListItemText>
      </StyledListItem>
    </Tooltip>
  </StyledList>
);

export const MatchPopoverButton = ({
  onMatchSelection,
  fileInputRef,
  hasFileSelected = false,
  hasAssetSelected = false,
}: {
  onMatchSelection: (match: Asset | FileUpload) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  hasFileSelected?: boolean;
  hasAssetSelected?: boolean;
}) => {
  const theme = useTheme();
  const { isImageSearchCroppingDisabled } = useFeatureFlags();
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const location = useLocation();
  const { fileUpload, setFileUpload, addFile, removeFile } = useFileUpload();
  const { permissions } = useGetCurrentUser();

  const {
    open: openAddMatch,
    close: closeAddMatch,
    isOpen: isAddMatchOpen,
    anchorElement: addMatchAnchorElement,
  } = usePopover();

  const [isAssetSelectionModalOpen, setIsAssetSelectionModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | undefined>();
  const webcamReference = useRef<Webcam>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const selectedFiles = fileUpload.files ?? [];

  const handleAddMatchClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    openAddMatch(event);
  };

  const handleCloseAddMatch = () => {
    closeAddMatch();
  };

  const handleStartMatchAsset = () => {
    setFileUpload({ files: undefined, useCase: undefined });
    handleCloseAddMatch();
    setIsAssetSelectionModalOpen(true);
  };

  const handleAssetSelect = (selectedAsset: Asset) => {
    if (selectedAsset.type !== AssetType.Document) {
      onMatchSelection(selectedAsset);
      setIsAssetSelectionModalOpen(false);
    }
  };

  const handleFileUpload = () => {
    handleCloseAddMatch();
    fileInputRef.current?.click();
  };

  const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files || [])];
    const availableSlots = MAX_IMAGES - selectedFiles.length;
    const filesToAdd = files.slice(0, availableSlots);

    if (filesToAdd.length > 0) {
      if (isImageSearchCroppingDisabled) {
        handleEditorComplete(filesToAdd);
      } else {
        setPendingFiles(filesToAdd);
        setIsEditorOpen(true);
      }
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const handlePhotoCapture = () => {
    handleCloseAddMatch();
    setIsImageModalOpen(true);
    setIsCameraOpen(true);
    setCapturedImage(undefined);
  };

  const handleCameraButtonClick = () => {
    setIsCameraOpen(true);
    setCapturedImage(undefined);
  };

  const handleCapture = () => {
    const imageSource = webcamReference.current?.getScreenshot();
    if (imageSource) {
      setCapturedImage(imageSource);
    }
  };

  const handleAcceptImage = () => {
    if (capturedImage && selectedFiles.length < MAX_IMAGES) {
      const [header, base64] = capturedImage.split(',');
      const mime = header.split(':')[1]?.split(';')[0] ?? 'image/jpeg';
      const bytes = atob(base64);
      const buffer = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index++) {
        buffer[index] = bytes.codePointAt(index)!;
      }
      const blob = new Blob([buffer], { type: mime });
      const timestamp = Date.now();
      const file = new File([blob], `camera-capture-${timestamp}.jpg`, { type: 'image/jpeg' });
      handleImageModalClose();
      if (isImageSearchCroppingDisabled) {
        handleEditorComplete([file]);
      } else {
        setPendingFiles([file]);
        setIsEditorOpen(true);
      }
    }
  };

  const handleRetakePhoto = () => {
    setCapturedImage(undefined);
  };

  const handleRemovePhoto = (fileIndex: number) => {
    const fileToRemove = selectedFiles[fileIndex];
    if (fileToRemove) {
      removeFile(fileToRemove);
      const newSelectedFiles = selectedFiles.filter((_, index) => index !== fileIndex);
      onMatchSelection({ files: newSelectedFiles, useCase: 'visual-search' });
    }
  };

  const handleImageModalClose = () => {
    setIsImageModalOpen(false);
    setIsCameraOpen(false);
    setCapturedImage(undefined);
  };

  const handleEditorComplete = (editedFiles: File[]) => {
    for (const file of editedFiles) {
      addFile(file);
    }
    setFileUpload({ ...fileUpload, useCase: 'visual-search' });
    onMatchSelection({ files: [...selectedFiles, ...editedFiles], useCase: 'visual-search' });

    if (location.pathname !== getPath('assets')) {
      navigate(getPath('assets'));
    }

    setPendingFiles([]);
    setIsEditorOpen(false);
  };

  const handleEditorCancel = () => {
    setPendingFiles([]);
    setIsEditorOpen(false);
  };

  const title: string = isCameraOpen ? (capturedImage ? 'Review Photo' : 'Take a Photo') : 'Take a Photo';
  const canAddMoreImages = selectedFiles.length < MAX_IMAGES;

  return (
    <Box margin='0 0 0 6px'>
      <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
        <Tooltip title='Add files'>
          <RoundedSquareIconButton
            size='small'
            data-testid={SearchPageObjectModel.MatchPopoverButton}
            onClick={handleAddMatchClick}
            sx={{ color: theme.palette.text.secondary }}
          >
            <Add sx={{ fontSize: '18px' }} />
          </RoundedSquareIconButton>
        </Tooltip>
      </label>
      <StyledPopover
        open={isAddMatchOpen}
        anchorEl={addMatchAnchorElement}
        onClose={handleCloseAddMatch}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: '20px',
              marginTop: '0px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <AddMatchMenu
          canOnlySearch={permissions.canOnlySearch}
          hasFileSelected={hasFileSelected}
          hasAssetSelected={hasAssetSelected}
          onSelectAsset={handleStartMatchAsset}
          onUploadFile={handleFileUpload}
          onTakePhoto={handlePhotoCapture}
        />
      </StyledPopover>
      <AssetSelectionModal
        open={isAssetSelectionModalOpen}
        onClose={() => setIsAssetSelectionModalOpen(false)}
        onAssetSelect={handleAssetSelect}
        getRowClassName={getRowClassName}
        title='Select Asset for Matching'
        description='Choose an asset to use for geometric or visual matching. Documents are not available for selection.'
      />
      <Dialog
        open={isImageModalOpen}
        onClose={handleImageModalClose}
        maxWidth='sm'
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>{title}</Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
          {isCameraOpen ? (
            capturedImage ? (
              <ImageReview
                imageSrc={capturedImage}
                onRetake={handleRetakePhoto}
                onAccept={handleAcceptImage}
                onClose={handleImageModalClose}
              />
            ) : (
              <>
                {canAddMoreImages ? (
                  <CameraView webcamRef={webcamReference} onCapture={handleCapture} onClose={handleImageModalClose} />
                ) : (
                  <BodyText>You have {MAX_IMAGES} images ready for search. Remove an image below to add more.</BodyText>
                )}
                <SelectedFilesThumbnails files={selectedFiles} onRemove={handleRemovePhoto} />
              </>
            )
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant='outlined'
                onClick={handleCameraButtonClick}
                startIcon={<CameraAlt />}
                fullWidth
                sx={{ justifyContent: 'flex-start', p: 2 }}
                disabled={!canAddMoreImages}
              >
                Take Photo
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileUploadChange}
        accept={ALLOWED_IMAGE_FORMATS}
        multiple
        style={{ display: 'none' }}
      />
      {isEditorOpen && pendingFiles.length > 0 && (
        <ImageEditorModal
          open={isEditorOpen}
          files={pendingFiles}
          onComplete={handleEditorComplete}
          onCancel={handleEditorCancel}
        />
      )}
    </Box>
  );
};
